import { create } from 'zustand';
import type { Me, PlayerPreferences, Session } from '@cribbage/protocol';
import { ApiError, api, type PolicyAcceptance } from '~/api/client.js';
import { type Agreement, deviceAgreement, useAgreementStore } from '~/legal/agreement.js';
import { flight } from '~/session/singleFlight.js';
import { initPlatform } from '~/platform/bootstrap.js';
import { platform } from '~/platform/index.js';
import { type AvatarId, ownFace } from '~/store/avatars.js';
import { claimUnownedGames } from '~/store/localGames.js';
import { forgetPlayerData } from '~/store/playerData.js';
import { claimLegacyOptOut } from '~/store/settingsStore.js';
import { type FriendlyError, toFriendlyError } from '~/store/sessionStore.js';

/**
 * Who this device is playing as.
 *
 * There is always somebody once the device has agreed to the terms. A launch
 * with no token registers the device, and the server answers with a device, an
 * anonymous player and a session for it, so the app is never without a player
 * to record a game against. Signing in swaps that player for the account's;
 * signing out swaps it back.
 *
 * Before the agreement there is nobody (D10). The Welcome screen stands in
 * front of everything that would need a player, and its tap is what registers
 * the device, with the agreement attached. See `legal/agreement.ts`.
 *
 * One token, always. The server never leaves two live sessions on one device,
 * so `token` here is the whole of what this device holds, and `user.anonymous`
 * is the only thing that says whether there is an account behind it.
 *
 * The bearer token is kept in `localStorage` rather than a cookie: the same
 * build runs inside a Capacitor WebView where cookie handling across the
 * native bridge is a source of subtle bugs, and the token is already opaque
 * and revocable server-side.
 */
const TOKEN_KEY = 'cribbage.token';

/**
 * The player the stored token belongs to, written and removed with it.
 *
 * `user` arrives only once `/me` answers, and a launch with no network never
 * gets that far. What the device keeps for its player, its saved games and the
 * Resume door's marker, is keyed on the player id. Without this a launch in
 * airplane mode could not find the game it was in the middle of. See
 * `devicePlayerId`.
 */
const PLAYER_KEY = 'cribbage.player';

/**
 * Written and removed before a registration, to find out whether this browser
 * will keep anything at all. See `canKeepToken`.
 */
const PROBE_KEY = 'cribbage.storage-probe';

/**
 * The player a deletion is for and the token it was sent with, read before the
 * request goes out. See `leaveDeletedAccount`.
 */
export interface Departing {
  token: string | null;
  playerId: string;
}

export interface AuthState {
  token: string | null;
  user: Me | null;
  loading: boolean;
  /**
   * True once the first session attempt has settled, however it went.
   *
   * A screen that redirects on "no account" has to be able to tell that from
   * "the device has not registered yet", which is the first second of a fresh
   * install and looks identical without this.
   */
  ready: boolean;
  error: FriendlyError | null;

  restore(): Promise<void>;
  register(email: string, password: string, displayName: string): Promise<boolean>;
  login(email: string, password: string): Promise<boolean>;
  /**
   * Reads `/me` again. Answers with the `/me` this request adopted, or null
   * when it failed or arrived for a session the device no longer holds. The
   * store may hold a later answer by the time the caller reads it.
   */
  refresh(): Promise<Me | null>;
  /**
   * Hands the account session back. False when the device is still signed in,
   * which is what a sign-out with no network leaves it as.
   */
  logout(): Promise<boolean>;
  /** True while a sign-out is in the air, so the buttons can say so. */
  signingOut: boolean;
  /**
   * Sends one change to the player's own record and adopts the answer.
   *
   * The refusal comes back instead of landing on `error`: each settings row
   * owns the line under its own field, and `error` belongs to the screen that
   * signs a player in.
   */
  saveMe(call: () => Promise<Me>): Promise<FriendlyError | null>;
  /**
   * Leave an account the server has just deleted.
   *
   * A sign-out first, which hands back this device's own guest. If that cannot
   * reach the server the deleted account must still not stay on the device:
   * its token is dead and its player is gone, and a game recorded under it
   * would belong to nobody. So the token is dropped and the device registers
   * a guest of its own. True when a guest is playing afterwards.
   *
   * `confirmed` says the server took the deletion. Only then are the account's
   * games and settings cleared from this device (D05): a session that merely
   * stopped working may belong to an account that still exists.
   *
   * `departing` is who asked, read before the request went out. The answer can
   * land after the player has signed in as somebody else, and that session is
   * left alone: only the departing player's data is cleared.
   */
  leaveDeletedAccount(departing: Departing, confirmed: boolean): Promise<boolean>;
  /**
   * Leave a guest whose data the server has just erased.
   *
   * The server answers the erasure with the session of the fresh guest it gave
   * this install, and that is the one taken up. With no session in hand (the
   * answer was lost, and the old token now answers 401) the device registers a
   * guest of its own, as a deleted account does. Either way the erased guest's
   * games and settings leave this device first. True when a guest is playing
   * afterwards. `departing` works as it does for `leaveDeletedAccount`.
   */
  leaveErasedGuest(departing: Departing, session: Session | undefined): Promise<boolean>;
  /**
   * Tells the server the Cross walkthrough is finished, and adopts its answer.
   * Never throws: a device that could not reach the server keeps its own record
   * and sends the news again later. See `useCrossInvitation`.
   */
  completeCrossTour(): Promise<void>;
  /**
   * The device has just agreed. Registers it when it has no player yet, with
   * the agreement attached, and otherwise records the agreement for the player
   * it holds. Never throws: offline, the record waits on the device and goes up
   * with the next registration or launch.
   */
  confirmAgreement(): Promise<void>;
  /**
   * Sends the Game settings to the server and adopts its answer. `refused` is
   * a server that will not take them, which sending again will not change;
   * `failed` is anything that might go through next time. Never throws. See
   * `preferenceSync.ts`.
   */
  savePreferences(preferences: PlayerPreferences): Promise<'saved' | 'refused' | 'failed'>;
  clearError(): void;
}

/**
 * One restore at a time, however many callers ask.
 *
 * `restore` can mint a device, which mints a player and a session, and it is
 * the one call in the app that is not idempotent on the server. React's
 * StrictMode runs the mounting effect twice in development, so without this
 * every dev reload created two devices and two players and threw one of each
 * away. A second caller waits on the first rather than starting a second one.
 */
const restoring = flight();

/**
 * One sign-out at a time, for the same reason as `restore`.
 *
 * `logout` is a *switch*: the server revokes the account session and issues the
 * device's local one in the same call. Two of them in flight means two switches,
 * and whichever response lands second is the token this device keeps — which is
 * the one the server revoked when it issued the other. A double tap on the
 * sign-out button left the app holding a dead token. The second caller waits on
 * the first and gets its answer.
 */
const loggingOut = flight<boolean>();

/**
 * One enrolment at a time, for the same reason as `restore` — and for one more.
 *
 * `refresh` answers a 401 by calling `forget()` and enrolling again. If that
 * lands while a sign-in is still in flight, the sign-in has not written its
 * token yet, so `refresh`'s own currency guard passes and the enrolment starts:
 * a second device and a second player, and whichever response settles last
 * decides which one the install keeps. The other is orphaned, and the player
 * either appears signed out or loses the install they just signed in on.
 *
 * Enrollment and account requests also wait for each other. A recovery that
 * starts after sign-in must recheck the token before minting another device;
 * sign-in that starts after enrollment must use the device it just created.
 */
const enrolling = flight<boolean>();
const signingIn = flight<boolean>();

/**
 * One walkthrough report at a time. The Cross lesson sends it when its game
 * ends; a second report while the first is out joins it.
 */
const reportingCrossTour = flight();

/**
 * Agreements in the air, by player and record. A sign-in and a launch can both
 * ask for the same one; the second waits for nothing and sends nothing.
 */
const recordingAgreements = new Set<string>();

/** The device's record in the shape the server takes. */
function acceptanceOf(agreement: Agreement): PolicyAcceptance {
  return {
    terms_version: agreement.termsVersion,
    privacy_version: agreement.privacyVersion,
    accepted_at: agreement.acceptedAt,
    platform: platform().name,
  };
}

/** How long a preference save may take before it counts as failed and waits for the next try. */
const PREFERENCE_SAVE_TIMEOUT_MS = 15_000;

export const useAuthStore = create<AuthState>((set, get) => {
  const hold = (session: Session): void => {
    writeToken(session.token, session.user.id);
    api.setToken(session.token);
    // The first moment there is a player id to hang a pre-v7 sync refusal on.
    claimLegacyOptOut(session.user.id);
    set({ token: session.token, user: session.user, loading: false, ready: true });
    // A sign-in or sign-out can hand over a player the server has no agreement
    // from yet. This device agreed, so it says so for that player too.
    void recordAgreement(session.user.id);
  };

  /**
   * Send the device's agreement for the player the current session holds,
   * once. Called the moment that player is known. A failure keeps the record
   * on the device, and the next launch or sign-in sends it again.
   */
  const recordAgreement = async (player: string): Promise<void> => {
    const agreement = deviceAgreement();
    if (agreement === null || agreement.recordedFor.includes(player)) return;
    const key = `${player}@${agreement.acceptedAt}`;
    if (recordingAgreements.has(key)) return;
    recordingAgreements.add(key);
    try {
      await api.recordAcceptance(acceptanceOf(agreement));
      useAgreementStore.getState().markRecorded(player, agreement);
    } catch {
      // Offline, or refused. The record stays on the device.
    } finally {
      recordingAgreements.delete(key);
    }
  };

  const adopt = (call: () => Promise<Session>): Promise<boolean> => {
    const enrollment = enrolling.current();
    set({ loading: true, error: null });
    return signingIn.after(async () => {
      // Sign-in queues behind other sign-ins, and also behind an enrollment
      // already out: one that started first must not mint a second device on
      // top of the account this call is about to hold.
      // Swallowed for the same reason `flight`'s own predecessor wait swallows:
      // a failure ahead of us is that caller's to report, not a reason to fail
      // this sign-in. Inert today, because everything `enrollOnce` can reach is
      // caught, but two of its `set` calls sit outside that `try`.
      if (enrollment !== null) await enrollment.catch(() => undefined);
      set({ loading: true, error: null });
      try {
        hold(await call());
        return true;
      } catch (error) {
        set({ loading: false, error: toFriendlyError(error) });
        return false;
      }
    });
  };

  const waitForSignIn = (): Promise<void> => signingIn.idle();
  /**
   * Whether the store still holds `token` once any sign-in has settled. Every
   * answer that lands after a wait is checked here before it is acted on: the
   * player may have signed in or out while it was in flight.
   */
  const stillHolding = async (token: string | null): Promise<boolean> => {
    await waitForSignIn();
    return get().token === token;
  };

  /*
   * Preference saves acknowledged since this store was made. A `/me` answer that
   * left before a save landed carries the server's copy from before it, so
   * `adoptFor` keeps the device's newer copy rather than rolling it back.
   */
  let preferenceSaves = 0;

  /**
   * Run a request that answers with this player's record, and adopt the answer.
   *
   * The answer is applied only for the token it was sent with: one that lands
   * after a sign-out belongs to the account that left, and adopting it would put
   * that account back on the device (see `refresh`). False when the answer was
   * not adopted. Errors from the request are the caller's to read.
   */
  const adoptFor = async (issuedFor: string | null, call: () => Promise<Me>): Promise<boolean> => {
    const savesAtStart = preferenceSaves;
    const user = await call();
    if (!(await stillHolding(issuedFor))) return false;
    set({ user: newerPreferences(user, savesAtStart) });
    return true;
  };

  /**
   * The answer, keeping the device's preferences if a save was acknowledged
   * after the request left: the answer's copy is older than that save.
   */
  const newerPreferences = (user: Me, savesAtStart: number): Me => {
    const current = get().user;
    const savedSince = savesAtStart !== preferenceSaves && current?.id === user.id;
    return savedSince ? { ...user, preferences: current.preferences } : user;
  };

  /**
   * Come back as this device's own player.
   *
   * Both ways of having no session end here: a first launch, and a token the
   * server no longer honours. The old token is dropped first, because a
   * registration is the one call that must not go out under it.
   *
   * A failure is not an error a player can act on: it means the launch happened
   * with no network. Offline play still works, the games wait on the device, and
   * the next resume or the next sync tries again rather than the app sitting
   * unregistered until somebody reloads it.
   *
   * `firstEver` says this device has never held a token, which is the one
   * moment when the games already on it can only belong to the player being
   * minted. See `claimUnownedGames`.
   *
   * Nothing is discarded on the way in. The registration carries no bearer
   * token of its own, so there is no reason to drop the one this device holds
   * before asking, and every reason not to: a failure here used to leave the
   * device with no token at all while the session it threw away was still live
   * on the server. Dropping a token is `forget`'s job, and only the callers
   * that know the token is dead do it.
   */
  const enroll = ({ firstEver = false } = {}): Promise<boolean> => {
    if (signingIn.current() !== null) {
      return waitForSignIn().then(() => (get().token !== null ? true : enroll({ firstEver })));
    }
    return enrolling.join(() => enrollOnce({ firstEver }));
  };

  const enrollOnce = async ({ firstEver = false } = {}): Promise<boolean> => {
    /*
     * A browser that will not keep the token must not be given one.
     *
     * `registerDevice` mints a device, a player and a session on the server,
     * and `hold` then writes the token — through a `writeToken` that swallows a
     * storage failure, because a session that does not survive a reload is
     * still better than a crash. It is not better than never registering: the
     * player exists, is unreachable the moment the page reloads, and the next
     * launch mints another one behind it. Blocked storage is not rare (a
     * locked-down browser, some private modes), so the orphans accumulate.
     *
     * So probe first, and when the probe fails do nothing: offline play works
     * without a player, and `PRIVACY.md` already says a browser that will not
     * keep the identifier sends nothing.
     */
    if (!canKeepToken()) {
      set({ loading: false, ready: true });
      return false;
    }
    /*
     * Nor is a device that has not agreed to the terms (D10). Its player would
     * be created, and its user agent written down, before the player said yes
     * to anything. The Welcome screen's tap comes back through here.
     */
    const agreement = deviceAgreement();
    if (agreement === null) {
      set({ loading: false, ready: true });
      return false;
    }
    set({ loading: true });
    try {
      // The device row's platform is stamped at create and never revisited, so
      // wait for the native adapter to be installed before asking which one
      // this is. On the web this is already resolved and costs nothing.
      await initPlatform();
      const session = await api.registerDevice(platform().name, acceptanceOf(agreement));
      // The registration carried the record, so `hold` has nothing to send.
      useAgreementStore.getState().markRecorded(session.user.id, agreement);
      hold(session);
      if (firstEver) await claimUnownedGames(session.user.id);
      return true;
    } catch {
      set({ loading: false, ready: true });
      return false;
    }
  };

  /** Drop what this device is holding. Only for a token known to be dead. */
  const forget = (): void => {
    writeToken(null, null);
    api.setToken(null);
    set({ token: null, user: null });
  };

  return {
    token: readToken(),
    user: null,
    loading: false,
    ready: false,
    error: null,
    signingOut: false,

    async restore() {
      await restoring.join(async () => {
        const token = readToken();
        if (token === null) {
          /*
           * No token in storage, so this device has never held one: either it
           * has just been installed, or a launch with no network could not get
           * one. Both are the case `claimUnownedGames` is for.
           */
          await enroll({ firstEver: true });
          return;
        }
        api.setToken(token);
        set({ token, loading: true });
        try {
          // Not `adoptFor`: a launch also claims the legacy marker and stores
          // the player. It keeps the same rule about a newer preference save.
          const savesAtStart = preferenceSaves;
          const user = await api.me();
          // Answers for the token we sent. See refresh().
          if (!(await stillHolding(token))) return;
          // This path does not go through `hold` — the token was already in
          // storage — so it has to claim the legacy marker itself. Left to
          // `hold`, an upgrading install would spend the refusal on whichever
          // player the next sign-out happened to hand back.
          claimLegacyOptOut(user.id);
          // An install from before the player was stored learns it here.
          writePlayer(token, user.id);
          set({ user: newerPreferences(user, savesAtStart), loading: false, ready: true });
          // An agreement made offline, or after a terms change, goes up now.
          void recordAgreement(user.id);
        } catch (error) {
          if (!(await stillHolding(token))) return;
          // A revoked or expired token is not a state to sit in: register the
          // device again rather than leave the app with nobody to play as.
          if (error instanceof ApiError && error.isAuthFailure) {
            forget();
            await enroll();
            return;
          }
          set({ loading: false, ready: true, error: toFriendlyError(error) });
        }
      });
    },

    register: (email, password, displayName) =>
      adopt(() => api.register(email, password, displayName)),

    login: (email, password) => adopt(() => api.login(email, password)),

    /**
     * Hand the account session back and take the device's local one.
     *
     * The server issues that local session in the same call and returns it, so
     * this is one swap rather than a sign-out followed by a gap. A session with
     * no device behind it has no local player to return to, and the device
     * registers from scratch instead.
     *
     * The account token is never thrown away before there is something to put
     * in its place. A sign-out that could not reach the server left this device
     * holding nothing while the session it discarded went on being live: signed
     * out on the phone, signed in everywhere the server can see. So a request
     * that did not land leaves the player signed in, and says why.
     */
    async logout() {
      const inAir = loggingOut.current();
      if (inAir !== null) return inAir;

      set({ signingOut: true });
      // The flag settles inside the operation, so it clears in the same
      // callback that frees the slot. Hung off the returned promise instead,
      // there is a window where the slot is free and `signingOut` is still
      // true, and a second sign-out could start inside it.
      return loggingOut.join(async () => {
        try {
          let session: Session | undefined;
          try {
            session = await api.logout();
          } catch (error) {
            /*
             * A 401 is the exception, and it is the account-deletion path: the
             * session is already not live, so there is nothing left to preserve
             * and nothing to hand back. Everything else, a dropped connection or
             * a server error, leaves the account session in a state we cannot
             * see.
             */
            if (!(error instanceof ApiError) || !error.isAuthFailure) {
              set({ loading: false, error: toFriendlyError(error) });
              return false;
            }
          }
          set({ error: null });
          if (session === undefined) {
            // Either a 204 (a private window, with no device to come back as) or
            // a session already revoked. The token is spent either way, so
            // register first and drop it only if that is what we end up with.
            if (!(await enroll())) forget();
            return true;
          }
          hold(session);
          return true;
        } finally {
          set({ signingOut: false });
        }
      });
    },

    /*
     * Both of these answer for the token they were sent with, and a reply that
     * arrives after the token has moved on is discarded rather than applied.
     * Profile mounts and calls refresh(), so signing out while it is in flight
     * is an ordinary sequence, not a contrived one — and applying its answer
     * would leave {token: the new anonymous one, user: the old account}. Games
     * recorded next are stamped with the old player and uploaded under the new
     * token, which the server refuses as `wrong_player` and the client files as
     * `held`, so they never settle. The 401 branch is worse: a stale auth
     * failure would call forget() and throw away the token logout just issued.
     */
    async refresh() {
      const issuedFor = get().token;
      if (issuedFor === null) return null;
      let answer: Me | null = null;
      try {
        const adopted = await adoptFor(issuedFor, async () => {
          answer = await api.me();
          return answer;
        });
        return adopted ? answer : null;
      } catch (error) {
        if (!(await stillHolding(issuedFor))) return null;
        if (error instanceof ApiError && error.isAuthFailure) {
          forget();
          await enroll();
        }
        return null;
      }
    },

    async leaveDeletedAccount(departing, confirmed) {
      if (confirmed) await forgetPlayerData(departing.playerId);
      if (!(await stillHolding(departing.token))) return get().user !== null;
      if (await get().logout()) return get().user !== null;
      /*
       * The sign-out may have failed long after it started. By then a refresh
       * may already have swapped the dead token for a guest, or the player may
       * have signed in to another account: that session is not this one to
       * throw away. Only the deleted account's own token is dropped.
       */
      if (!(await stillHolding(departing.token))) return get().user !== null;
      forget();
      set({ error: null });
      return enroll();
    },

    async leaveErasedGuest(departing, session) {
      await forgetPlayerData(departing.playerId);
      if (!(await stillHolding(departing.token))) return get().user !== null;
      if (session !== undefined) {
        hold(session);
        set({ error: null });
        return true;
      }
      forget();
      set({ error: null });
      return enroll();
    },

    /*
     * Answers for the token it was sent with, like `refresh`: a reply
     * that lands after a sign-out belongs to the account that was signed out,
     * and adopting it would put that account's record back on the device.
     */
    async saveMe(call) {
      try {
        await adoptFor(get().token, call);
        return null;
      } catch (error) {
        return toFriendlyError(error);
      }
    },

    async completeCrossTour() {
      await reportingCrossTour.join(async () => {
        const issuedFor = get().token;
        if (issuedFor === null) return;
        try {
          await adoptFor(issuedFor, () => api.completeCrossTour());
        } catch {
          // No network, or no answer. The device's own record stands, and
          // useCrossInvitation sends it again.
        }
      });
    },

    async confirmAgreement() {
      const { token, user } = get();
      if (token === null) {
        /*
         * Not ready again until the registration answers. The gate opens on
         * the tap, so an invite or game link mounts its screen at once, and a
         * screen that sends a device with no session to sign in reads
         * `ready && token === null` as "this device has nobody". Every exit
         * from `enrollOnce` sets `ready` back.
         */
        set({ ready: false });
        // `firstEver`: games stored before this device had a player are its own.
        await enroll({ firstEver: true });
        return;
      }
      // With no user yet, the launch's restore is still asking who this is,
      // and it records the agreement when it hears.
      if (user !== null) await recordAgreement(user.id);
    },

    async savePreferences(preferences) {
      const issuedFor = get().token;
      if (issuedFor === null) return 'failed';
      try {
        // Bounded, because sends queue one behind another: a request that never
        // settled would hold every later save back.
        const signal = AbortSignal.timeout(PREFERENCE_SAVE_TIMEOUT_MS);
        if (!(await adoptFor(issuedFor, () => api.updatePreferences(preferences, signal)))) {
          return 'failed';
        }
        preferenceSaves += 1;
        return 'saved';
      } catch (error) {
        // A malformed or invalid document. A sign-in failure or a throttle is
        // worth another try once the session or the server has recovered.
        return error instanceof ApiError && (error.status === 400 || error.status === 422)
          ? 'refused'
          : 'failed';
      }
    },

    clearError() {
      set({ error: null });
    },
  };
});

/**
 * The player this device is playing as, or null before the first session.
 *
 * This is the id everything gameplay-shaped is keyed on: the seat in a lobby,
 * the row in a game's participations, the stamp on a game recorded offline. It
 * is never a user id, and an account's player keeps it across sign-outs.
 */
export function usePlayerId(): string | null {
  return useAuthStore((state) => state.user?.id ?? null);
}

/** The same id outside React, for the stores that stamp records with it. */
export function playerId(): string | null {
  return useAuthStore.getState().user?.id ?? null;
}

/**
 * The face the signed-in player chose, for a component that draws heads. It
 * follows `/me`: a launch draws the Fox until the server answers, then the
 * chosen face, so a caller passes it on, into any memo too, rather than
 * reading the store once.
 */
export function useOwnFace(): AvatarId {
  return useAuthStore((state) => ownFace(state.user?.avatar_id));
}

/**
 * The player this device holds a session for, known before `/me` answers.
 *
 * `usePlayerId` waits for the server. This falls back to the id stored with
 * the token, so it answers on a launch with no network, and on a reload before
 * the session is restored. It is for reading what the device already keeps
 * for its player, such as a game to resume. It does not stamp new records:
 * that stays `playerId`'s job, and a player the server has not confirmed does
 * not get to own a new game.
 */
export function useDevicePlayerId(): string | null {
  return useAuthStore((state) => state.user?.id ?? storedPlayer(state.token));
}

/** The same id outside React. */
export function devicePlayerId(): string | null {
  const { user, token } = useAuthStore.getState();
  return user?.id ?? storedPlayer(token);
}

/**
 * True while there is no account behind the player.
 *
 * Unknown counts as anonymous: it is the first moment of a fresh install, and
 * the wrong way to be wrong there is to offer an account-only surface to
 * somebody who has no account.
 */
export function useAnonymous(): boolean {
  return useAuthStore((state) => state.user?.anonymous ?? true);
}

/**
 * Whether this browser will hold on to a value.
 *
 * A real write and a real removal, because the failure modes are not visible
 * any other way: `localStorage` can be missing, can throw on access, and can
 * accept a read while refusing every write once its quota is zero.
 */
function canKeepToken(): boolean {
  try {
    globalThis.localStorage.setItem(PROBE_KEY, '1');
    globalThis.localStorage.removeItem(PROBE_KEY);
    return true;
  } catch {
    return false;
  }
}

function readToken(): string | null {
  try {
    return globalThis.localStorage?.getItem(TOKEN_KEY) ?? null;
  } catch {
    return null;
  }
}

/** The token and its player go in and out together. */
function writeToken(token: string | null, player: string | null): void {
  try {
    if (token === null) globalThis.localStorage?.removeItem(TOKEN_KEY);
    else globalThis.localStorage?.setItem(TOKEN_KEY, token);
    if (token === null || player === null) globalThis.localStorage?.removeItem(PLAYER_KEY);
    else globalThis.localStorage?.setItem(PLAYER_KEY, player);
  } catch {
    /* private browsing: the session simply does not survive a reload */
  }
}

/**
 * Store the player for a token this device still holds, and touch nothing else.
 *
 * `restore` answers for the token it read at launch, and another tab may have
 * signed in or out since. Rewriting the token here would put a revoked one
 * back over the newer session.
 */
function writePlayer(token: string, player: string): void {
  try {
    if (globalThis.localStorage?.getItem(TOKEN_KEY) === token) {
      globalThis.localStorage.setItem(PLAYER_KEY, player);
    }
  } catch {
    /* private browsing: the player is learned again on the next launch */
  }
}

/** The stored player, and only while there is a token for it to belong to. */
function storedPlayer(token: string | null): string | null {
  if (token === null) return null;
  try {
    return globalThis.localStorage?.getItem(PLAYER_KEY) ?? null;
  } catch {
    return null;
  }
}
